import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { X, ArrowRight, ArrowLeft, Compass } from "lucide-react";

const DONE_KEY = "tx_tour_done";

async function buildSteps() {
  let runId = null, dealId = null;
  try {
    const runs = await endpoints.runs();
    runId = (runs.find((r) => r.status === "awaiting_approval") || runs[0])?.id || null;
  } catch {}
  try {
    const { deals } = await endpoints.handoffDeals();
    dealId = deals.find((d) => d.run_id)?.id || deals[0]?.id || null;
  } catch {}
  const steps = [
    { route: "/", target: "page-overview", title: "Private Offer Autopilot — the CPPO Execution Engine", body: "An autonomous agent (Claude Sonnet 5) designs AWS Channel Partner Private Offers across Salesforce, AWS ACE, AMMP and MCP sales agents. This dashboard tracks TCV, authorizations and runs for the ISV workspace." },
    { route: "/agent", target: "agent-intent-input", title: "1 · State an intent, dispatch the agent", body: "Plain-language intent in, autonomous tool-calling out. The agent streams every reasoning token and tool call live over Server-Sent Events." },
    runId && { route: `/agent?run=${runId}`, target: "agent-trace", title: "2 · Watch the live trace", body: "CRM lookup → ACE partners → CP registration → MCP hints → rate card → incentive playbook → drafted authorization. Everything is persisted for audit." },
    runId && { route: `/agent?run=${runId}`, target: "open-approve-dialog", optional: true, title: "3 · The irreversible gate", body: "Publishing an AWS Selling Authorization can't be undone, so the agent always stops here. An owner must type PUBLISH — then the authorization is submitted to AMMP and the CPPO is released." },
    { route: "/handoff", target: "deals-list", title: "4 · Deal hand-offs", body: "Qualified deals from Marketplace Deal Options arrive via API key, become agent runs instantly, and status flows back through signed webhooks." },
    dealId && { route: `/handoff/${dealId}`, target: "stage-rail", title: "5 · One deal, one timeline", body: "Hand-off → agent → approval gate → authorization → CPPO release, including owner notifications and webhook deliveries, on a single timeline." },
    { route: "/playbooks", target: "playbook-analytics", title: "6 · Incentive playbooks & analytics", body: "The agent structures each CPPO to maximize the AWS Marketplace incentive (EDP burn-down, MAP credits, co-sell…). Analytics show which playbooks drive released TCV." },
    { route: "/audit", target: "seals-table", title: "7 · Signed audit trail", body: "Any run exports as a PDF sealed with SHA-256 + HMAC, verifiable publicly at /verify — built for procurement and legal review." },
  ].filter(Boolean);
  return steps;
}

export default function DemoTour({ enabled }) {
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(-1);
  const [rect, setRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const timer = useRef(null);

  const start = useCallback(async () => {
    const s = await buildSteps();
    setSteps(s);
    setIdx(0);
  }, []);

  useEffect(() => {
    if (enabled && !localStorage.getItem(DONE_KEY)) {
      const t = setTimeout(start, 1200);
      return () => clearTimeout(t);
    }
  }, [enabled, start]);

  useEffect(() => {
    const onStart = () => start();
    window.addEventListener("tx:start-tour", onStart);
    return () => window.removeEventListener("tx:start-tour", onStart);
  }, [start]);

  const step = idx >= 0 ? steps[idx] : null;

  useEffect(() => {
    if (!step) return;
    const want = step.route;
    const here = location.pathname + location.search;
    if (here !== want) navigate(want);
    let tries = 0;
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      tries += 1;
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "auto" });
        setTimeout(() => setRect(el.getBoundingClientRect()), 150);
        clearInterval(timer.current);
      } else if (tries > 25) {
        clearInterval(timer.current);
        if (step.optional) setIdx((i) => Math.min(i + 1, steps.length - 1));
        else setRect(null);
      }
    }, 120);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, step?.route, step?.target]);

  useEffect(() => {
    if (!step) return;
    const update = () => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [step]);

  const finish = () => {
    localStorage.setItem(DONE_KEY, "1");
    setIdx(-1);
    setRect(null);
  };

  if (!step) return null;
  const pad = 10;
  const vw = window.innerWidth, vh = window.innerHeight;
  const card = { w: 400, h: 220 };
  let top = rect ? rect.bottom + 16 : vh / 2 - card.h / 2;
  let left = rect ? rect.left : vw / 2 - card.w / 2;
  if (rect && top + card.h > vh - 16) top = rect.top - card.h - 16;
  top = Math.min(Math.max(16, top), vh - card.h - 16);
  if (left + card.w > vw - 16) left = vw - card.w - 16;
  left = Math.max(16, left);

  return (
    <div className="fixed inset-0 z-[100]" data-testid="demo-tour">
      {rect ? (
        <div
          className="absolute rounded-sm transition-all duration-300 pointer-events-none"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
                   boxShadow: "0 0 0 9999px rgba(24,24,27,0.55), 0 0 0 2px #ea580c" }}
        />
      ) : (
        <div className="absolute inset-0 bg-zinc-900/55" />
      )}
      <div
        data-testid="tour-card"
        className="absolute bg-white border border-zinc-200 shadow-2xl p-5 rounded-sm tx-fade-in"
        style={{ top, left, width: card.w }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="tx-overline flex items-center gap-1.5"><Compass className="w-3 h-3 text-orange-600" /> Guided tour · {idx + 1}/{steps.length}</span>
          <button onClick={finish} data-testid="tour-close" className="text-zinc-400 hover:text-zinc-900"><X className="w-4 h-4" /></button>
        </div>
        <div className="font-heading font-black text-lg text-zinc-900 mb-1.5" data-testid="tour-title">{step.title}</div>
        <p className="text-[13px] text-zinc-600 leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {steps.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-orange-500" : "bg-zinc-300"}`} />)}
          </div>
          <div className="flex gap-2">
            {idx > 0 && (
              <button onClick={() => setIdx(idx - 1)} data-testid="tour-back" className="h-8 px-3 border border-zinc-300 text-[12px] hover:bg-zinc-50 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</button>
            )}
            {idx < steps.length - 1 ? (
              <button onClick={() => setIdx(idx + 1)} data-testid="tour-next" className="h-8 px-3 bg-zinc-900 text-white text-[12px] hover:bg-zinc-700 flex items-center gap-1">Next <ArrowRight className="w-3 h-3" /></button>
            ) : (
              <button onClick={finish} data-testid="tour-finish" className="h-8 px-3 bg-orange-600 text-white text-[12px] hover:bg-orange-500">Start exploring</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const startTour = () => window.dispatchEvent(new Event("tx:start-tour"));
